import React from 'react'
import { Link } from 'react-router-dom'
import SiteHeader from '../components/SiteHeader'
import SiteFooter from '../components/SiteFooter'

const SECTIONS = [
  {
    title: '1. Acceptance of Terms',
    content: `By accessing or using the Notary Signing Vault, LLC platform ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Service. These Terms apply to all users including clients, notary signing agents, and visitors.`
  },
  {
    title: '2. Description of Service',
    content: `Notary Signing Vault, LLC ("NSV", "we", "us", or "our") is a nationwide mobile notary signing agency that connects clients with certified notary signing agents. We facilitate the scheduling, coordination, and completion of notarization services including but not limited to loan signings, affidavits, power of attorney documents, and other legal documents requiring notarization.`
  },
  {
    title: '3. User Accounts',
    content: `To access certain features of our platform, you must create an account. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account. We reserve the right to terminate accounts that violate these Terms.`
  },
  {
    title: '4. Notary Services',
    content: `NSV coordinates notary signing services through a network of independent contractor notary signing agents. All notaries on our platform are independently verified for valid commission, E&O insurance, and background checks. However, NSV does not guarantee the outcome of any notarization or signing. Notary signing agents are independent contractors, not employees of NSV.`
  },
  {
    title: '5. Fees and Payment',
    content: `Service fees are disclosed prior to confirmation of any order. Payment is due upon completion of services unless otherwise agreed in writing. All fees are in US dollars. Veterans and active military personnel may be eligible for a 50% discount upon verification. NSV reserves the right to modify pricing at any time with reasonable notice. Refunds are handled on a case-by-case basis.`
  },
  {
    title: '6. Cancellation Policy',
    content: `Cancellations made more than 24 hours before a scheduled signing will receive a full refund. Cancellations made within 24 hours of a scheduled signing may be subject to a cancellation fee of up to 50% of the service fee to compensate the assigned notary for their time. Same-day cancellations or no-shows will be charged the full service fee. Rush orders are non-refundable once a notary has been assigned.`
  },
  {
    title: '7. Independent Contractor Relationship',
    content: `Notary signing agents who register on the NSV platform are independent contractors. Nothing in these Terms creates an employment, partnership, joint venture, or franchise relationship between NSV and any notary. Notaries are responsible for their own taxes, insurance, and compliance with applicable laws and regulations in their state of commission.`
  },
  {
    title: '8. Confidentiality',
    content: `All parties agree to maintain strict confidentiality regarding borrower information, loan details, and any non-public personal information encountered during the course of providing services. Notaries must comply with all applicable privacy laws including but not limited to the Gramm-Leach-Bliley Act (GLBA). Client information will not be shared with third parties except as necessary to provide the requested services or as required by law.`
  },
  {
    title: '9. Document Retention',
    content: `Uploaded documents are retained for a period of 60 days following completion of services, after which they are automatically deleted from our systems. Users are responsible for maintaining their own copies of all important documents. NSV is not responsible for documents that are lost or inaccessible after the retention period.`
  },
  {
    title: '10. Limitation of Liability',
    content: `To the maximum extent permitted by applicable law, NSV shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or relating to your use of our Service. Our total liability for any claim arising out of these Terms shall not exceed the amount paid by you for the specific service giving rise to the claim in the 30 days preceding the claim.`
  },
  {
    title: '11. Indemnification',
    content: `You agree to indemnify and hold harmless Notary Signing Vault, LLC, its officers, directors, employees, and agents from any claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising out of or relating to your use of the Service, your violation of these Terms, or your violation of any rights of another party.`
  },
  {
    title: '12. Governing Law',
    content: `These Terms shall be governed by and construed in accordance with the laws of the State of Florida, without regard to its conflict of law provisions. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the state and federal courts located in Duval County, Florida.`
  },
  {
    title: '13. Modifications',
    content: `NSV reserves the right to modify these Terms at any time. We will notify registered users of material changes via email. Your continued use of the Service after such modifications constitutes your acceptance of the updated Terms. We encourage you to review these Terms periodically.`
  },
  {
    title: '14. Contact Information',
    content: `If you have any questions about these Terms of Service, please contact us at:\n\nNotary Signing Vault, LLC\nNancy Phillips, Founder & CEO\nJacksonville, Florida\nPhone: (904) 865-0133\nEmail: nancy@notarysigningvault.com\nWebsite: notarysigningvault.com`
  },
]

export default function TermsOfService() {
  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    :focus-visible { outline: 2px solid #4D4E8D; outline-offset: 3px; border-radius: 6px; }
    .tos-hero { background:#E9EAEC; padding:56px 24px 36px; text-align:center; font-family:'Plus Jakarta Sans',sans-serif; }
    .tos-hero h1 { font-family:'Plus Jakarta Sans',sans-serif; font-size:clamp(30px,3.6vw,40px); font-weight:800; color:#151715; margin-bottom:8px; letter-spacing:-.025em; }
    .tos-hero-sub { font-size:13px; color:#8A8F94; font-weight:500; }
    .tos-hero-gold { display:block; width:44px; height:3px; background:#4D4E8D; border-radius:2px; margin:16px auto; }
    .tos-body { background:#E9EAEC; min-height:100vh; font-family:'Plus Jakarta Sans',sans-serif; }
    .tos-wrap { max-width:760px; margin:0 auto; padding:16px 24px 72px; }
    .tos-section { margin-bottom:30px; }
    .tos-section h2 { font-size:16px; font-weight:800; color:#151715; margin-bottom:12px; padding-bottom:8px; border-bottom:2px solid #4D4E8D; display:inline-block; letter-spacing:-.01em; }
    .tos-section p { font-size:14px; color:#5C6065; font-weight:500; line-height:1.8; white-space:pre-line; }
    @media(max-width:640px){ .tos-hero h1 { font-size:26px; } }
  `

  return (
    <div style={{ background: '#E9EAEC', minHeight: '100vh' }}>
      <style>{styles}</style>

      {/* Nav */}
      <SiteHeader />

      {/* Hero */}
      <div className="tos-hero">
        <div style={{ fontSize: 10, letterSpacing: '0.2em', color: '#4D4E8D', marginBottom: 12 }}>LEGAL</div>
        <span className="tos-hero-gold" aria-hidden="true" />
        <h1>Terms of Service</h1>
        <p className="tos-hero-sub">Effective Date: April 18, 2026 · Last Updated: April 18, 2026</p>
      </div>

      {/* Body */}
      <div className="tos-body" id="nsv-main">
        <div className="tos-wrap">

          {/* Intro */}
          <div style={{ background: '#fff', border: '1px solid #E2E4E7', borderRadius: 14, padding: '20px 24px', marginBottom: 32 }}>
            <p style={{ fontSize: 14, color: '#3A3D40', lineHeight: 1.8 }}>
              Welcome to Notary Signing Vault, LLC. Please read these Terms of Service carefully before using our platform. 
              By creating an account or using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms.
            </p>
          </div>

          {/* Sections */}
          {SECTIONS.map((s, i) => (
            <div key={i} className="tos-section">
              <h2>{s.title}</h2>
              <p>{s.content}</p>
            </div>
          ))}

          {/* Signature */}
          <div style={{ background: '#EEF0F8', border:'1px solid #C7CBE2', borderRadius: 14, padding: '22px 24px', textAlign: 'center' }}>
            <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 18, fontWeight:800, color: '#4D4E8D', marginBottom: 4 }}>Notary Signing Vault, LLC</div>
            <div style={{ fontSize: 12, fontWeight:500, color: '#5C6065' }}>Nancy Phillips — Founder & CEO · Jacksonville, Florida</div>
            <div style={{ fontSize: 11, fontWeight:600, color: '#5E8A72', marginTop: 8 }}>Veteran & Hispanic Owned Business</div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <SiteFooter />
    </div>
  )
}
